import { useState } from 'react';
import { MapPin, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormatting';
import { StopArrivalBadge } from './StopArrivalBadge';
import { Button } from './ui/button';

// Shared "Route Progress" list for haulage dockets. Renders each stop with its
// status, ETA, actual-arrival badge, address, arrive/complete timestamps, notes
// and proof-of-delivery images. Used by the Dockets view/edit sheets and the
// Schedule View Docket drawer so the three stay in sync.
// Soft-deleted stops are shown struck-through with the audit trail (who + why)
// and can be restored by the dispatcher when `api` is supplied.
export const RouteProgressList = ({
  docket,
  tenantSettings = {},
  variant = 'bordered', // 'bordered' (divider rows in a boxed list) | 'cards' (each stop in a muted card)
  containerTestId = 'docket-route-progress',
  rowTestIdPrefix = 'docket-stop',
  api = null,
  onStopsChanged,
}) => {
  const [restoringId, setRestoringId] = useState(null);

  if (!docket || docket.job_type !== 'haulage' || !docket.job_stops || docket.job_stops.length === 0) {
    return null;
  }

  const fmt = (ts) => (ts ? formatDateTime(ts, tenantSettings.date_format, tenantSettings.time_format) : '—');
  const sorted = [...docket.job_stops].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleRestore = async (stopId) => {
    if (!api || !docket.job_id) return;
    setRestoringId(stopId);
    try {
      const res = await api.post(`/jobs/${docket.job_id}/stops/${stopId}/restore`);
      onStopsChanged && onStopsChanged(res.data.stops, res.data.route_summary);
      toast.success('Stop restored');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore stop');
    } finally {
      setRestoringId(null);
    }
  };

  const renderDeletedInner = (stop, idx) => (
    <div className="flex items-start gap-3 text-sm">
      <span className="font-mono text-xs text-muted-foreground mt-0.5">{idx + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="capitalize font-medium line-through text-muted-foreground">{stop.type}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600" data-testid={`${rowTestIdPrefix}-${idx}-deleted-badge`}>Deleted</span>
        </div>
        <p className="text-muted-foreground truncate line-through">
          {[stop.address, stop.suburb, stop.state, stop.postcode].filter(Boolean).join(', ') || stop.label || '—'}
        </p>
        <p className="text-xs text-red-600 mt-0.5" data-testid={`${rowTestIdPrefix}-${idx}-deleted-reason`}>
          Deleted by {stop.deleted_by_name || 'Driver'}
          {stop.deleted_at ? ` on ${fmt(stop.deleted_at)}` : ''}
          {stop.deleted_reason ? ` — ${stop.deleted_reason}` : ''}
        </p>
      </div>
      {api && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 shrink-0"
          disabled={restoringId === stop.id}
          onClick={() => handleRestore(stop.id)}
          data-testid={`${rowTestIdPrefix}-${idx}-restore-btn`}
        >
          {restoringId === stop.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Undo</>}
        </Button>
      )}
    </div>
  );

  const renderStopInner = (stop, idx) => {
    if (stop.deleted) return renderDeletedInner(stop, idx);
    const statusColor = stop.status === 'completed' ? 'text-green-600' : stop.status === 'arrived' ? 'text-blue-600' : 'text-muted-foreground';
    return (
      <div className="flex items-start gap-3 text-sm">
        <span className="font-mono text-xs text-muted-foreground mt-0.5">{idx + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="capitalize font-medium">{stop.type}</span>
            <span className={`text-xs ${statusColor} capitalize`}>· {stop.status || 'pending'}</span>
            {stop.est_arrival && <span className="text-xs text-muted-foreground">ETA {stop.est_arrival}</span>}
            <StopArrivalBadge stop={stop} />
          </div>
          <p className="text-muted-foreground truncate">
            {[stop.address, stop.suburb, stop.state, stop.postcode].filter(Boolean).join(', ') || '—'}
          </p>
          <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground mt-0.5">
            <span>Arrived: {fmt(stop.arrived_at)}</span>
            <span>Completed: {fmt(stop.completed_at)}</span>
            {stop.signed_by_name && <span>Signed: {stop.signed_by_name}</span>}
          </div>
          {stop.notes && <p className="text-xs text-muted-foreground italic mt-0.5">{stop.notes}</p>}
          {(stop.signature_url || (stop.photo_urls && stop.photo_urls.length > 0)) && (
            <div className="flex flex-wrap gap-2 mt-1">
              {stop.signature_url && (
                <img src={stop.signature_url} alt="Signature" className="h-12 border rounded bg-white" />
              )}
              {(stop.photo_urls || []).map((url, i) => (
                <img key={i} src={url} alt="POD" className="h-12 w-12 object-cover border rounded" />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid={containerTestId}>
      <h3 className="font-heading font-semibold mb-2 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-[#ffd108]" />
        Route Progress
      </h3>
      {docket.start_signoff && ['start', 'both', 'anytime'].includes(docket.signature_timing) && (
        <div className="mb-2 p-2 rounded-lg border border-green-600/30 bg-green-500/5" data-testid={`${containerTestId}-start-signoff`}>
          {docket.start_signoff.skipped ? (
            <p className="text-xs text-amber-600">{docket.signature_timing === 'anytime' ? 'Customer authorisation' : 'Start-of-day authorisation'} skipped — {docket.start_signoff.skip_reason === 'no_one_on_site' ? 'No one on site' : docket.start_signoff.skip_reason === 'customer_refused' ? 'Customer refused' : 'Other'}{docket.start_signoff.skip_notes ? ` (${docket.start_signoff.skip_notes})` : ''}</p>
          ) : (
            <div className="flex items-center gap-3">
              {docket.start_signoff.signature && <img src={docket.start_signoff.signature} alt="Customer authorisation signature" className="h-10 bg-white rounded border" />}
              <p className="text-xs text-muted-foreground">{docket.signature_timing === 'anytime' ? 'Authorised' : 'Authorised at start of day'} by <span className="font-medium text-foreground">{docket.start_signoff.customer_name}</span>{docket.start_signoff.signed_at ? ` · ${fmt(docket.start_signoff.signed_at)}` : ''}</p>
            </div>
          )}
        </div>
      )}
      {docket.route_summary && (
        <p className="text-xs text-muted-foreground mb-2">{docket.route_summary}</p>
      )}
      <div className={variant === 'cards' ? 'space-y-2' : 'space-y-2 p-3 border rounded-lg'}>
        {sorted.map((stop, idx) =>
          variant === 'cards' ? (
            <div key={stop.id || idx} className={`p-3 rounded-lg ${stop.deleted ? 'bg-red-500/5 border border-red-500/30' : 'bg-muted/50'}`} data-testid={`${rowTestIdPrefix}-${idx}`}>
              {renderStopInner(stop, idx)}
            </div>
          ) : (
            <div key={stop.id || idx} className="border-b last:border-b-0 pb-2 last:pb-0" data-testid={`${rowTestIdPrefix}-${idx}`}>
              {renderStopInner(stop, idx)}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RouteProgressList;
